import { useMutation } from '@tanstack/react-query'
import { postAsync } from '@/helpers/http-request'
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

type LoginData = {
  username: string;
  password: string;
  rememberMe: boolean;
}

type LoginError = {
  type: 'invalid' | 'locked';
}

const useBackendLogin = (returnUrl: string) => {
  const { t } = useTranslation();

  const loginMutation = useMutation({
    mutationFn: async (data: LoginData): Promise<void> => {
      const response = await postAsync('/web/v1/signIn/backend', data)

      if (!response.ok) {
        const responseData = await response.json()
        throw {type: responseData.lockedOut ? 'locked' : 'invalid'}
      }
    },
    onSuccess: (): void => {
      window.location.href = returnUrl
    },
    onError: (error: LoginError) => {
      if (error.type === 'invalid') {
        toast.error(t('errors.invalidCredentials'));
      } else if (error.type === 'locked') {
        toast.error(t('errors.accountLocked'));
      }
      loginMutation.reset();
    }
  });

  const errorType = loginMutation.error ? (loginMutation.error as unknown as LoginError).type : null;

  return {
    login: loginMutation.mutateAsync,
    isLoading: loginMutation.isPending,
    isInvalidCredentials: errorType === 'invalid',
    isLockedOut: errorType === 'locked',
    reset: loginMutation.reset,
  };
}

export default useBackendLogin;